import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { actionsKeys, cn, incidentStatus, permissionActions, ROLE_BOM, ROLE_OWNER, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetIncidentList } from "@/services";
import { Check, Edit, MoreVertical, X } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { priorityOptions } from "./consts";

const useIncidentMgmt = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState<any>();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [currentTab, setCurrentTab] = useState<string>("responsible");

  const {
    communityData: communityDetail,
    currentRole,
    loggedInUserData,
    accessPermission
  } = useSelector((state: RootState) => state.currentData || {});

  const isBom = useMemo(() => currentRole === ROLE_BOM, [currentRole]);

  const hasAddPermission = useMemo(() => {
    return isBom 
      ? accessPermission?.[actionsKeys?.INCIDENT_MANAGEMENT?.toLowerCase()]?.[permissionActions.CREATE] 
      : true;
  }, [isBom, accessPermission]);

  const hasUpdatePermission = useMemo(() => {
    return isBom 
      ? accessPermission?.[actionsKeys?.INCIDENT_MANAGEMENT?.toLowerCase()]?.[permissionActions.UPDATE] 
      : true;
  }, [isBom, accessPermission]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Construct filters safely with optional chaining and existence checks
  const filters = useMemo(() => {
    const baseFilters = [];

    if (communityDetail?.communityId) {
      baseFilters.push({
        field: "communityId",
        value: communityDetail.communityId,
        operator: "EQUAL",
      });
    }

    if (currentRole === ROLE_OWNER && loggedInUserData?.userId) {
      if (currentTab === "raised") {
        baseFilters.push({
          field: "reportedBy",
          value: loggedInUserData.userId,
          operator: "EQUAL",
        });
      } else {
        baseFilters.push({
          field: "assignedTo",
          value: loggedInUserData.userId,
          operator: "EQUAL",
        });
      }
    }

    return baseFilters;
  }, [communityDetail?.communityId, currentRole, currentTab, loggedInUserData?.userId]);

  const {
    data: incidentList,
    isLoading: isIncidentLoading,
    refetch: refetchIncident,
    isRefetching: isIncidentRefetching,
  } = useGetIncidentList({
    communityId: communityDetail?.communityId,
    data: {
      filters,
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      // Safely ensure both community ID and user ID exist before executing
      enabled: Boolean(communityDetail?.communityId && (currentRole !== ROLE_OWNER || loggedInUserData?.userId)),
    },
  });

  const onCurrentTabChange = (tab: string) => {
    setCurrentTab(tab);
    setPageNumber(0); // Reset page on tab change
  };

  useEffect(() => {
    if (communityDetail?.communityId) {
      refetchIncident();
    }
  }, [pageNumber, pageSize, sorting, currentTab, communityDetail?.communityId]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length === 0) {
      if (communityDetail?.communityId) {
        refetchIncident();
      }
    }
  }, [globalFilter]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "incidentId",
      header: "ID",
      accessorFn: (row: any) => String(row?.incidentId || ""),
    },
    {
      accessorKey: "incidentType",
      header: "Type",
      accessorFn: (row: any) => String(row?.incidentType || ""),
    },
    {
      accessorKey: "incidentCategoryName",
      header: "Category",
      accessorFn: (row: any) => String(row?.incidentCategoryName || ""),
    },
    {
      accessorKey: "commonAreaFlag",
      header: "Common Area",
      cell: (row: any) =>
        row?.row?.original?.commonAreaFlag ? (
          <Check className="h-[20px] w-[20px] stroke-green-600" />
        ) : (
          <X className="h-[20px] w-[20px] stroke-red-600" />
        ),
    },
    {
      accessorKey: "assignedToUserName",
      header: "Responsible",
      accessorFn: (row: any) => String(row?.assignedToUserName || ""),
    },
    {
      accessorKey: "priority",
      header: "Priority",
      accessorFn: (row: any) =>
        row?.priority
          ? priorityOptions.find(
              (data: any) =>
                data.value?.toLowerCase() === row.priority?.toLowerCase()
            )?.label
          : "",
    },
    {
      accessorKey: "createdAt",
      header: "Raised On",
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "reportedByUserName",
      header: "Raised By",
      accessorFn: (row: any) => String(row?.reportedByUserName || ""),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        const statusKey = row?.row?.original?.status?.toLowerCase();
        const statusConfig = (incidentStatus as any)?.[statusKey];
        return (
          <Badge
            variant="outline"
            className={cn(
              statusConfig?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {statusConfig?.label || row?.row?.original?.status}
          </Badge>
        );
      },
    },
    ...(hasUpdatePermission ? [{
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                disabled={row.row.original.status?.toLowerCase() == "resolved" || row?.row?.original?.status?.toLowerCase() === "charged_closed"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`${row.row.original.incidentId}/${routes.edit}`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    }] : [])
  ];

  const raisedColumns = [
    {
      accessorKey: "incidentId",
      header: "ID",
      accessorFn: (row: any) => String(row?.incidentId || ""),
    },
    {
      accessorKey: "incidentType",
      header: "Type",
      accessorFn: (row: any) => String(row?.incidentType || ""),
    },
    {
      accessorKey: "incidentCategoryName",
      header: "Category",
      accessorFn: (row: any) => String(row?.incidentCategoryName || ""),
    },
    {
      accessorKey: "createdAt",
      header: "Raised On",
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        const statusKey = row?.row?.original?.status?.toLowerCase();
        const statusConfig = (incidentStatus as any)?.[statusKey];
        return (
          <Badge
            variant="outline"
            className={cn(
              statusConfig?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {statusConfig?.label || row?.row?.original?.status}
          </Badge>
        );
      },
    },
  ];

  const responsibleColumns = [
    {
      accessorKey: "incidentId",
      header: "ID",
      accessorFn: (row: any) => String(row?.incidentId || ""),
    },
    {
      accessorKey: "incidentType",
      header: "Type",
      accessorFn: (row: any) => String(row?.incidentType || ""),
    },
    {
      accessorKey: "incidentCategoryName",
      header: "Category",
      accessorFn: (row: any) => String(row?.incidentCategoryName || ""),
    },
    {
      accessorKey: "createdAt",
      header: "Raised On",
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "updatedAt",
      header: "Last Updated",
      accessorFn: (row: any) =>
        row?.updatedAt ? moment(row.updatedAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        const statusKey = row?.row?.original?.status?.toLowerCase();
        const statusConfig = (incidentStatus as any)?.[statusKey];
        return (
          <Badge
            variant="outline"
            className={cn(
              statusConfig?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {statusConfig?.label || row?.row?.original?.status}
          </Badge>
        );
      },
    },
  ];

  const onAddIncident = () => {
    navigate(routes.add);
  };

  const isEditDisabled = (row: any) =>
    row?.original?.status?.toLowerCase() === "resolved" ||
    row?.original?.status?.toLowerCase() === "charged_closed";

  const onRowClick = (row: any) => {
    if (isBom) {
      if (hasUpdatePermission && !isEditDisabled(row)) {
        navigate(`${row?.original?.incidentId}/${routes.edit}`);
        return;
      }

      navigate(`${row?.original?.incidentId}/${routes.view}`);
      return;
    }

    if (
      currentTab === "raised" ||
      (currentTab === "responsible" && isEditDisabled(row))
    ) {
      navigate(`${row?.original?.incidentId}/${routes.view}`);
      return;
    }

    if (hasUpdatePermission) {
      navigate(`${row?.original?.incidentId}/${routes.edit}`);
      return;
    }

    navigate(`${row?.original?.incidentId}/${routes.view}`);
  };

  const totalRecords = incidentList?.total || 0;
  const totalPages = pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0;
  const tableData = incidentList?.data?.sort(
    (a: any, b: any) =>
      new Date(b.updatedAt).getTime() -
      new Date(a.updatedAt).getTime()
  ) || [];

  return {
    communityDetail,
    columns,
    isMobile,
    globalFilter,
    setGlobalFilter,
    onAddIncident,
    onRowClick,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    currentRole,
    raisedColumns,
    responsibleColumns,
    onCurrentTabChange,
    currentTab,
    hasAddPermission,
    totalRecords,
    totalPages,
    tableData,
    isLoading: isIncidentLoading || isIncidentRefetching
  };
};

export default useIncidentMgmt;
